import React, { useState, useRef, useEffect } from 'react';
import { FileText, Upload, Trash2, Loader2, X } from 'lucide-react';
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import ThreeDViewerModal from '../modals/ThreeDViewerModal';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_CAD_TYPES = [
  '.dxf',
  '.dwg',
  '.iges',
  '.step',
  '.x-iges',
  '.x-step',
  '.x-stp',
];

const CADFileUploader = ({
  screenState,
  onFileUpload,
  onFileDelete,
  initialKey = null,
  classNames = 'w-full h-32',
  label = 'Upload CAD File',
  id = 'cad-file-input',
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);

  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [displayProgress, setDisplayProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [fileKey, setFileKey] = useState(initialKey);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [uniqueKey, setUniqueKey] = useState(null);

  const animationFrameRef = useRef();
  const startProgressRef = useRef(0);
  const targetProgressRef = useRef(0);
  const startTimeRef = useRef(null);

  useEffect(() => {
    cancelAnimationFrame(animationFrameRef.current);
    startProgressRef.current = displayProgress;
    targetProgressRef.current = progress;
    startTimeRef.current = performance.now();

    const animate = (time) => {
      const t = Math.min((time - startTimeRef.current) / 2000, 1);
      const easedT = t < 0.5 ? 4 * t ** 3 : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const newProgress =
        startProgressRef.current +
        (targetProgressRef.current - startProgressRef.current) * easedT;
      setDisplayProgress(newProgress);
      if (t < 1) animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [progress]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    setError('');
    setIsUploading(true);
    setProgress(0);
    await uploadFile(selectedFile);
  };

  const uploadFile = async (selectedFile) => {
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const { data } = await apiClient.post('/api/cad/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: ({ loaded, total }) =>
          setProgress(Math.round((loaded / total) * 100)),
      });

      const { cad_file_conversion_id, output_scs_s3_key } = data.data;
      setFileKey(cad_file_conversion_id);
      setFile(selectedFile);
      onFileUpload?.({
        cad_conversion_id: cad_file_conversion_id,
        fileKey: output_scs_s3_key,
      });
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to upload CAD file');
    } finally {
      setIsUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    setIsDeleting(true);
    try {
      setFile(null);
      setFileKey(null);
      onFileDelete?.();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to delete CAD file');
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePreview = () => {
    if (fileKey) fetchPreviewUrl(fileKey);
    else setError('No file selected for preview');
  };

  const fetchPreviewUrl = async (key) => {
    try {
      const { data } = await apiClient.post(
        '/api/cad/get',
        JSON.stringify({ cad_file_conversion_id: key }),
      );
      setUniqueKey(data.data.output_scs_s3_key);
      setIsViewerOpen(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch preview URL');
    }
  };

  return (
    <div className="cad-file-uploader w-full">
      <div className="relative group flex flex-col">
        {file || fileKey ? (
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <div
                  className={`relative flex flex-col justify-center items-center ${classNames} border border-lightPurple hover:border-opacity-70 border-opacity-30 rounded-md cursor-pointer overflow-hidden`}
                  onClick={handlePreview}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="h-8 w-8 text-blue-600" />
                  </div>
                  <span className="text-xs text-center p-2 truncate absolute bottom-0 w-full bg-lightPurple bg-opacity-60 text-white backdrop-blur-sm">
                    {file?.name || 'CAD File'}
                  </span>
                  {(screenState === 'EDIT_FORM' ||
                    screenState === 'CREATING_PART') && (
                    <button
                      type="button"
                      onClick={handleRemove}
                      disabled={isDeleting}
                      className="absolute top-1 right-1 bg-purple-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-gray-100 bg-opacity-45 p-2 rounded-md shadow-md translate-y-6">
                <p className="text-center text-xs text-gray-600">
                  Click to preview
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Label
            htmlFor={id}
            className={`${classNames} bg-white border-2 border-dashed border-gray-400 rounded-md flex flex-col items-center justify-center cursor-pointer text-muted-foreground hover:text-darkPurple hover:border-darkerPurple transition-colors`}
          >
            {isUploading ? (
              <div className="flex flex-col items-center justify-center w-full h-full space-y-2">
                <Loader2 className="h-6 w-6 text-lightPurple animate-spin" />
                {progress > 0 && (
                  <div className="relative w-3/4 h-1 bg-gray-200 rounded-md">
                    <div
                      className="absolute h-1 bg-lightPurple rounded-md"
                      style={{ width: `${displayProgress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex gap-2 items-center justify-center">
                <Upload className="h-4 mx-2 w-4" />
                <span className="text-sm text-center">{label}</span>
                <Input
                  id={id}
                  type="file"
                  className="hidden"
                  accept={ACCEPTED_CAD_TYPES.join(',')}
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
              </div>
            )}
          </Label>
        )}

        {error && (
          <div className="mt-2 bg-red-50 border border-red-200 rounded-md p-2 flex items-center justify-between">
            <span className="text-red-600 text-sm">{error}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-red-500"
              onClick={() => setError('')}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      <ThreeDViewerModal
        open={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        uniqueKey={uniqueKey}
        fileName={file?.name || 'CAD File'}
      />
    </div>
  );
};

export default CADFileUploader;
